import { IconButton } from "@mui/material";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useStyleCharacterDetails } from "./style-character-details";
import parse from "html-react-parser";
import { NotesForActionModal } from "@/widgets/product-pricing-widget/components/action/notes-for-action-modal";
import {
  useCharacterDetails,
  type ItemDetailsSummaryCollapseMode,
} from "./use-character-details";
import { getCompactDetailSummary } from "./split-item-details-summary";
import { ListUpDownIcon } from "@/icons/shared/list-up-down-icon";

interface CharacterDetailsProps {
  details: string;
  getQuote?: () => void;
  documentItemId?: string | number;
  detailsStyle?: React.CSSProperties;
  showAllStyle?: React.CSSProperties;
  showLessHeightFit?: boolean;
  canUpdate?: boolean;
  documentType?: number;
  workName?: string;
  isPrePaymentPage?: boolean;
  item?: any;
  defaultLanguage?: string;
  /** Mobile / card: summary = job + size; expand adds printing details and rest. */
  summaryCollapseMode?: ItemDetailsSummaryCollapseMode;
  /**
   * Opt-in for the modern Jobs table only. Default stays the existing
   * fixed-height details box used by quotes and the legacy Jobs table.
   */
  compactDescription?: boolean;
}

const CharacterDetails = ({
  details,
  getQuote,
  documentItemId,
  showAllStyle,
  detailsStyle,
  showLessHeightFit = false,
  canUpdate = true,
  documentType,
  workName,
  isPrePaymentPage,
  item,
  defaultLanguage,
  summaryCollapseMode = "default",
  compactDescription = false,
}: CharacterDetailsProps) => {
  const {
    t,
    isRTL,
    isEdit,
    showAll,
    data,
    setData,
    handleShowLess,
    handleShowMore,
    setIsEdit,
    handleClose,
    handleSave,
    jobName,
    setJobName,
    truncatedDetails,
    canNotEditWhenShowJobNameOnly,
    showExpandToggle,
    showTruncationEllipsis,
  } = useCharacterDetails({
    details,
    getQuote,
    documentItemId,
    documentType,
    workName,
    defaultLanguage,
    summaryCollapseMode,
  });

  const { classes } = useStyleCharacterDetails({ showAll, showLessHeightFit });
  const compactSummary = useMemo(
    () =>
      compactDescription
        ? getCompactDetailSummary(String(details ?? ""))
        : null,
    [compactDescription, details]
  );
  const compactBodyRef = useRef<HTMLDivElement>(null);
  const [compactLineOverflow, setCompactLineOverflow] = useState(false);

  useLayoutEffect(() => {
    if (!compactDescription || showAll) {
      return;
    }
    const body = compactBodyRef.current;
    if (!body) {
      return;
    }
    const measure = () => {
      const nodes = body.querySelectorAll<HTMLElement>("[data-desc-line]");
      setCompactLineOverflow(
        Array.from(nodes).some((el) => el.scrollWidth > el.clientWidth + 1)
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(body);
    return () => observer.disconnect();
  }, [compactDescription, showAll, details, compactSummary]);

  const canEditDetails =
    canUpdate && !canNotEditWhenShowJobNameOnly && !isPrePaymentPage;
  const renderedCompactLines = showAll
    ? compactSummary?.allLines ?? []
    : compactSummary?.previewLines ?? [];
  const hasHiddenCompactLines =
    (compactSummary?.allLines.length ?? 0) >
    (compactSummary?.previewLines.length ?? 0);
  const showCompactToggle = hasHiddenCompactLines || compactLineOverflow;

  return (
    <>
      <div
        onClick={canEditDetails ? () => setIsEdit(true) : undefined}
        style={{
          cursor: canEditDetails ? "pointer" : "default",
          position: "relative",
          textAlign: isRTL ? "right" : "left",
          ...detailsStyle,
          ...(compactDescription
            ? { width: "100%", minWidth: 0, maxWidth: "100%" }
            : {}),
        }}
      >
        {compactDescription ? (
          <div
            ref={compactBodyRef}
            className="gm-pf-desc-body"
            data-expanded={showAll ? "true" : "false"}
            style={{
              ...classes.mainContainer,
              height: "auto",
              maxHeight: "none",
              overflowX: "hidden",
              overflowY: showAll ? "visible" : "hidden",
              padding: 0,
              borderRight: "none",
              minWidth: 0,
              maxWidth: "100%",
              width: "100%",
            }}
          >
            {renderedCompactLines.map((line, index) => {
              const isLast = index === renderedCompactLines.length - 1;
              return (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 6,
                    minWidth: 0,
                    maxWidth: "100%",
                    width: "100%",
                  }}
                >
                  <div
                    data-desc-line=""
                    className="gm-pf-desc-line"
                    style={{
                      flex: "1 1 0%",
                      width: 0,
                      minWidth: 0,
                      maxWidth: "100%",
                      overflow: showAll ? "visible" : "hidden",
                      textOverflow: showAll ? "clip" : "ellipsis",
                      whiteSpace: showAll ? "normal" : "nowrap",
                      overflowWrap: showAll ? "anywhere" : "normal",
                    }}
                  >
                    {parse(line)}
                  </div>
                  {showCompactToggle && isLast && (
                    <span
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        showAll ? handleShowLess() : handleShowMore();
                      }}
                      style={{
                        ...(showAllStyle || classes.showAllContaner),
                        flex: "0 0 auto",
                        whiteSpace: "nowrap",
                        textDecoration: "underline",
                      }}
                    >
                      {showAll
                        ? t("sales.quote.showLess")
                        : t("sales.quote.showMore")}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ ...classes.mainContainer }}>
            {parse(String(truncatedDetails || ""))}
            {showTruncationEllipsis ? ".. " : null}
            {showExpandToggle && (
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  showAll ? handleShowLess() : handleShowMore();
                }}
                style={showAllStyle || { ...classes.showAllContaner }}
              >
                {showAll ? t("sales.quote.showLess") : t("sales.quote.showMore")}
              </span>
            )}
          </div>
        )}

        {canEditDetails && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setIsEdit(true);
            }}
            style={{
              position: "absolute",
              top: 0,
              [isRTL ? "left" : "right"]: 0,
            }}
          >
            <ListUpDownIcon color="var(--theme-primary-color, #2E3092)" />
          </IconButton>
        )}
      </div>
      <NotesForActionModal
        key={`notes-modal-${documentItemId}`}
        openModal={isEdit}
        onClose={handleClose}
        value={data}
        setValue={setData}
        onSend={handleSave}
        modalTitle={t("sales.cart.editDetails")}
        id={documentItemId}
        isHaveJobName={item?.printHouseMaterialId ? false : true}
        jobName={jobName}
        setJobName={setJobName}
        documentType={documentType}
        item={item}
        defaultLanguage={defaultLanguage}
      />
    </>
  );
};
export { CharacterDetails };
